import { ModelContract, ModuleDescription } from "../../shared/module-description";
import { IUserAuthContract } from "../../student/auth/model";
import { createEffect, createEvent, createStore, sample } from "effector";
import { reset } from "patronum";
import { CompletedLessonRequestRecord, ILesson } from "../../shared/entities/lessons";
import { UserEnrollmentStatus } from "@features/shared/entities/status";
import { $currentTopicId, ICurrentTopicContract } from "../../topics/ui/current";
import { ITopicsContract } from "../../topics/model";
import { ITopic } from "../../shared/entities/topics";
import { IStudentTopic, IStudentLesson } from "../../shared/entities/enrollments";
import { $enrollmentsLessons, IEnrollmentsContract } from "../../enrollments/model";
import { $currentCourse, $currentCourseId } from "@features/courses/ui/current";
import { toastShow } from "@features/notification/model";
import { ICustomError } from "@features/shared/entities/notification";
import { CourseDataApi } from "@features/shared/api/course-data.api";
import { LessonCompleted } from "@features/shared/events";
import { $streakFlowFlag, type StreakFlowVariant } from "@features/amplitude-flags/model";
import { routePathNameChanged } from "@features/app/model";

export interface ILessonWithStatus extends ILesson {
    enrollmentStatus: UserEnrollmentStatus | null;
    startedAt: string | null;
    completedAt: string | null;
}

export interface ITopicWithLessons {
    topicId: number;
    progress: number;
    title: string;
    description: string;
    lessons: ILessonWithStatus[];
}

export const lessonsLoaded = createEvent();
export const completeLesson = createEvent<CompletedLessonRequestRecord>();
const lessonCompletionTracked = createEvent();

export const $lessons = createStore<ILesson[]>([]);
export const $completedLessonId = createStore<number | null>(null);
export const $courseLessons = createStore<ITopicWithLessons[]>([]);
// Id of the course the data currently in $courseLessons belongs to. Lets the UI tell
// whether the loaded map matches the course being viewed (vs. stale data from the
// previous course while the new one is still loading), so it can show a skeleton.
const $courseLessonsCourseId = createStore<number | null>(null);
const $lastCompletedLessonInfo = createStore<{
    lesson: ILesson;
    topicId: number;
    courseId: number;
    courseType: string;
    courseTags: string[];
} | null>(null);
const $lastLessonId = createStore<null | number>(null);
const $lastLesson = createStore<ILessonWithStatus | null>(null);
const $totalLessonsInCourse = createStore<number | null>(null);
const $topicLessons = createStore<ILessonWithStatus[]>([]);
const $lastLessonIdInTopic = createStore<null | number>(null);
const $lastLessonInTopic = createStore<ILessonWithStatus | null>(null);

export const getLessonsByTopicIdFx = createEffect(async (topicId: number) => {
    const response = await CourseDataApi.getLessonsByTopicId(topicId);

    return response;
});

type CourseMapTopic = { topic: ITopic; lessons: ILesson[] };

const $courseMap = createStore<CourseMapTopic[]>([]);

const getLessonsForCourseFx = createEffect(
    (topics: ITopic[]): Promise<CourseMapTopic[]> =>
        Promise.all(
            topics.map((topic) =>
                CourseDataApi.getLessonsByTopicId(topic.id).then((lessons) => ({
                    topic,
                    lessons: lessons.sort((a, b) => a.number - b.number)
                }))
            )
        )
);

function withEnrollments(
    courseMap: CourseMapTopic[],
    enrollmentsTopics: IStudentTopic[],
    enrollmentsLessons: IStudentLesson[]
): ITopicWithLessons[] {
    return courseMap.map(({ topic, lessons }) => {
        const enrollment = enrollmentsTopics.find((et) => et.topic_id === topic.id);

        const enrichedLessons: ILessonWithStatus[] = lessons.map((lesson) => {
            const lessonEnrollment = enrollmentsLessons.find((el) => el.lesson_id === lesson.id);

            return {
                ...lesson,
                enrollmentStatus: lessonEnrollment?.status || null,
                startedAt: lessonEnrollment?.started_at || null,
                completedAt: lessonEnrollment?.completed_at || null
            };
        });

        return {
            topicId: topic.id,
            progress: enrollment?.progress || 0,
            title: topic.title,
            description: topic.description,
            lessons: enrichedLessons
        };
    });
}

export const lessonCompletedFx = createEffect(async (data: CompletedLessonRequestRecord) => {
    const response = await CourseDataApi.completeLesson(data);
    return response;
});

const trackLessonCompletedFx = createEffect(
    ({
        lesson,
        topicId,
        courseId,
        courseType,
        ver
    }: {
        lesson: ILesson;
        topicId: number;
        courseId: number;
        courseType: string;
        ver?: number;
    }) => {
        LessonCompleted({
            courseType,
            courseId,
            topicId,
            lessonId: lesson.id,
            lessonTitle: lesson.title,
            dayNumber: lesson.number,
            ver
        });
    }
);

sample({
    clock: completeLesson,
    fn: (data) => data.lessonId,
    target: $completedLessonId
});

sample({
    clock: getLessonsByTopicIdFx.doneData,
    fn: (lessons) => lessons.slice().sort((a, b) => a.number - b.number),
    target: [$lessons, lessonsLoaded]
});

// Enrich topic lessons with enrollments data whenever lessons or enrollments change
sample({
    source: { lessons: $lessons, enrollments: $enrollmentsLessons },
    filter: (s) => s.lessons.length > 0,
    fn: (s) => {
        if (!s.lessons || s.lessons.length === 0) return [] as ILessonWithStatus[];
        const byLessonId = new Map<number, IStudentLesson>();
        for (const e of s.enrollments) byLessonId.set(e.lesson_id, e);
        return s.lessons.map((l) => {
            const el = byLessonId.get(l.id);
            return {
                ...l,
                enrollmentStatus: el?.status ?? null,
                startedAt: el?.started_at ?? null,
                completedAt: el?.completed_at ?? null
            } as ILessonWithStatus;
        });
    },
    target: $topicLessons
});

sample({
    clock: getLessonsForCourseFx.doneData,
    target: $courseMap
});

// Tag the freshly loaded course map with the course it belongs to.
sample({
    clock: getLessonsForCourseFx.doneData,
    source: $currentCourseId,
    fn: (currentCourseId, courseMap) => courseMap[0]?.topic.course_id ?? currentCourseId,
    target: $courseLessonsCourseId
});

sample({
    clock: completeLesson,
    target: lessonCompletedFx
});

sample({
    clock: lessonCompletedFx.done,
    target: lessonCompletionTracked
});

sample({
    clock: [getLessonsByTopicIdFx.fail, lessonCompletedFx.fail, getLessonsForCourseFx.fail],
    filter: ({ error }: { error: ICustomError }) => error?.error?.title !== null,
    fn: ({ error }: { error: ICustomError }) => error?.error,
    target: toastShow
});

sample({
    source: {
        lessons: $lessons,
        completedLessonId: $completedLessonId,
        topicId: $currentTopicId,
        course: $currentCourse,
        streakFlow: $streakFlowFlag
    },
    clock: lessonCompletedFx.done,
    filter: ({ completedLessonId, topicId, course }) =>
        completedLessonId !== null && topicId !== null && course !== null,
    fn: ({ lessons, completedLessonId, topicId, course, streakFlow }) => {
        const lesson = lessons.find((l) => l.id === completedLessonId);
        const STREAK_FLOW_VER: Record<StreakFlowVariant, number | undefined> = {
            old: 1,
            daily_goal: 2,
            off: undefined
        };
        return {
            lesson: lesson!,
            topicId: topicId!,
            courseId: course!.id,
            courseType: course!.type,
            courseTags: course!.tags ?? [],
            ver: STREAK_FLOW_VER[streakFlow]
        };
    },
    target: [trackLessonCompletedFx, $lastCompletedLessonInfo]
});

sample({
    source: $courseLessons,
    filter: (course) => course.length > 0,
    fn: (course) => {
        const flat = course.flatMap((t) => t.lessons);
        if (flat.length === 0) return null;

        const isNull = (l: ILessonWithStatus) => l.enrollmentStatus === null;
        const isProgress = (l: ILessonWithStatus) => l.enrollmentStatus === UserEnrollmentStatus.Progress;
        const isCompleted = (l: ILessonWithStatus) => l.enrollmentStatus === UserEnrollmentStatus.Completed;

        // Newest COMPLETED by completedAt
        const completedWithIndex = flat
            .map((lesson, index) => ({ lesson, index }))
            .filter(({ lesson }) => isCompleted(lesson) && !!lesson.completedAt)
            .sort(
                (a, b) =>
                    new Date(a.lesson.completedAt as string).getTime() -
                    new Date(b.lesson.completedAt as string).getTime()
            );

        const lastCompletedIndex =
            completedWithIndex.length > 0 ? completedWithIndex[completedWithIndex.length - 1].index : -1;

        const isCandidate = (l: ILessonWithStatus) => isNull(l) || isProgress(l);

        if (lastCompletedIndex !== -1) {
            // Scan forward after the last COMPLETED to the end
            for (let i = lastCompletedIndex + 1; i < flat.length; i++) {
                if (isCandidate(flat[i])) return flat[i].id;
            }
            // Wrap to the start, up to the last COMPLETED
            for (let i = 0; i < lastCompletedIndex; i++) {
                if (isCandidate(flat[i])) return flat[i].id;
            }
            return null;
        }

        // No COMPLETED: pick newest PROGRESS by startedAt, else first lesson
        const newestProgress = flat
            .filter((l) => isProgress(l) && !!l.startedAt)
            .sort((a, b) => new Date(b.startedAt as string).getTime() - new Date(a.startedAt as string).getTime())[0];
        if (newestProgress) return newestProgress.id;
        return flat[0]?.id ?? null;
    },
    target: $lastLessonId
});

// Compute last lesson within current topic from $topicLessons
sample({
    source: $topicLessons,
    filter: (lessons) => lessons.length > 0,
    fn: (lessons) => {
        if (lessons.length === 0) return null;

        const isNull = (l: ILessonWithStatus) => l.enrollmentStatus === null;
        const isProgress = (l: ILessonWithStatus) => l.enrollmentStatus === UserEnrollmentStatus.Progress;
        const isCompleted = (l: ILessonWithStatus) => l.enrollmentStatus === UserEnrollmentStatus.Completed;

        // Newest COMPLETED by completedAt
        const completedWithIndex = lessons
            .map((lesson, index) => ({ lesson, index }))
            .filter(({ lesson }) => isCompleted(lesson) && !!lesson.completedAt)
            .sort(
                (a, b) =>
                    new Date(a.lesson.completedAt as string).getTime() -
                    new Date(b.lesson.completedAt as string).getTime()
            );

        const lastCompletedIndex =
            completedWithIndex.length > 0 ? completedWithIndex[completedWithIndex.length - 1].index : -1;

        const isCandidate = (l: ILessonWithStatus) => isNull(l) || isProgress(l);

        if (lastCompletedIndex !== -1) {
            // Scan forward after the last COMPLETED to the end
            for (let i = lastCompletedIndex + 1; i < lessons.length; i++) {
                if (isCandidate(lessons[i])) return lessons[i].id;
            }
            // Wrap to the start, up to the last COMPLETED
            for (let i = 0; i < lastCompletedIndex; i++) {
                if (isCandidate(lessons[i])) return lessons[i].id;
            }
            return null;
        }

        // No COMPLETED: pick newest PROGRESS by startedAt, else first lesson
        const newestProgress = lessons
            .filter((l) => isProgress(l) && !!l.startedAt)
            .sort((a, b) => new Date(b.startedAt as string).getTime() - new Date(a.startedAt as string).getTime())[0];
        if (newestProgress) return newestProgress.id;
        return lessons[0]?.id ?? null;
    },
    target: $lastLessonIdInTopic
});

// Resolve actual last lesson entity within topic by id
sample({
    source: $topicLessons,
    clock: $lastLessonIdInTopic,
    filter: (s, p) => s.length > 0,
    fn: (s, p) => {
        if (p === null) return null;
        return s.find((l) => l.id === p) ?? null;
    },
    target: $lastLessonInTopic
});

sample({
    clock: $lastLessonId,
    source: $courseLessons,
    filter: (s, p) => s.length > 0,
    fn: (s, p) => {
        if (p === null) return null;

        const flat = s.flatMap((t) => t.lessons);
        return flat.find((l) => l.id === p) ?? null;
    },
    target: $lastLesson
});

sample({
    clock: $courseLessons,
    filter: (s) => s.length > 0,
    fn: (s) => s.flatMap((t) => t.lessons).length,
    target: $totalLessonsInCourse
});

const contract = new ModelContract(
    {
        $lessons,
        $courseLessons,
        $courseLessonsCourseId,
        $topicLessons,
        $lastLessonIdInTopic,
        $lastLessonInTopic,
        $lastLessonId,
        $lastLesson,
        $lastCompletedLessonInfo,
        $totalLessonsInCourse
    },
    {
        completeLesson,
        lessonsLoaded,
        lessonCompletionTracked
    },
    { getLessonsByTopicIdFx, lessonCompletedFx, getLessonsForCourseFx },
    {
        getLessonsByTopicIdFx,
        lessonCompletedFx,
        getLessonsForCourseFx
    }
);

export type ILessonsContract = typeof contract;

export class LessonsModule extends ModuleDescription<ILessonsContract> {
    constructor(
        public userAuthModule: IUserAuthContract,
        public currentTopicModule: ICurrentTopicContract,
        public enrollmentsModule: IEnrollmentsContract,
        public topicsModule: ITopicsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: this.currentTopicModule.events.currentTopicIdChanged,
            filter: (topicId) => topicId !== null,
            fn: (topicId) => topicId as number,
            target: getLessonsByTopicIdFx
        });

        sample({
            source: this.topicsModule.stores.$topics,
            target: getLessonsForCourseFx
        });

        sample({
            source: {
                courseMap: $courseMap,
                enrollmentsTopics: this.enrollmentsModule.stores.$enrollmentsTopics,
                enrollmentsLessons: this.enrollmentsModule.stores.$enrollmentsLessons
            },
            fn: ({ courseMap, enrollmentsTopics, enrollmentsLessons }) =>
                withEnrollments(courseMap, enrollmentsTopics, enrollmentsLessons),
            target: $courseLessons
        });

        reset({
            clock: this.userAuthModule.events.logoutStudent,
            target: [$lessons, $courseMap, $courseLessons, $courseLessonsCourseId, $lastLessonId, $lastLesson]
        });

        reset({
            clock: routePathNameChanged,
            target: [$lessons, $topicLessons, $lastLessonId, $lastLesson, $lastLessonIdInTopic, $lastLessonInTopic]
        });
    }
}
